import React, { useState, useEffect, useRef, useMemo } from "react";
import AnimatedChart from "@/pages/quiz/base/components/animatedChart";
import { YourTreatmentOptionsModal } from "@/components/YourTreatmentOptionsModal";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { HeightWeightAnswer } from "@/pages/quiz/base/questions/heightWeightInputsWithBMI";
import { useQuizContext } from "@/pages/quiz/context";

const ChartYourPotentialWeightLoss = ({
  setQuestionAnswer,
}: QuizQuestionProps<boolean>) => {
  const { answers } = useQuizContext();

  const heightWeightInputFieldsWithBmiData = Object.values(answers).find(
    (answer) => answer.blockName === "height-weight-input-fields-with-bmi"
  )?.value as HeightWeightAnswer;

  const duration = 3000;
  const currentWeight = Number(heightWeightInputFieldsWithBmiData.weight);
  const goalWeight = Number(heightWeightInputFieldsWithBmiData.weight) * 0.8;
  const weightDifference = currentWeight - goalWeight;

  const [displayWeight, setDisplayWeight] = useState(0);
  const [displayDifference, setDisplayDifference] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [chartUp, setChartUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let startTime: number | null = null;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      setDisplayWeight(Math.floor(progress * goalWeight));
      setDisplayDifference(Math.floor(progress * weightDifference));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [currentWeight, weightDifference, duration]);

  useEffect(() => {
    const openModalTimer = setTimeout(
      () => setOpenModal(true),
      duration + 1000
    );
    return () => clearTimeout(openModalTimer);
  }, [duration]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setChartUp(true);
    }, duration + 1000);
    return () => clearTimeout(timer);
  }, [duration]);

  const handleContinue = () => {
    setOpenModal(false);
    setQuestionAnswer(true);
  };

  const date = useMemo(() => new Date(), []);

  return (
    <>
      <div
        ref={containerRef}
        className="flex flex-col items-start justify-center gap-5"
      >
        <div className="flex flex-col items-start justify-center w-full">
          <div className="flex items-baseline">
            <span className="text-[70px] w-[130px] font-bold inline-block">
              {displayWeight}
            </span>
            <span className="text-[70px] font-bold ml-2">lbs</span>
          </div>
          <p className="!text-lg text-second-text-v2">Potential new weight</p>

          <div className="flex text-[40px] font-medium text-green-500 items-baseline">
            <span className="min-w-[55px] inline-block">
              {displayDifference}
            </span>
            <span>lbs</span>
          </div>
          <p className="!text-lg text-second-text-v2">You could lose</p>

          <div
            className={`
              w-full 
              transition-transform duration-1000 
              ${chartUp ? "lg:-translate-y-10" : "translate-y-0"}
            `}
          >
            <AnimatedChart startDate={date} duration={duration} />
          </div>
        </div>
      </div>

      <YourTreatmentOptionsModal
        weightDifference={displayDifference}
        open={openModal}
        version={"v1"}
        onClose={handleContinue}
      />
    </>
  );
};

export { ChartYourPotentialWeightLoss };
